/* eslint-disable no-unused-vars */
import React, {useCallback, useContext, useEffect, useState} from 'react';
import {isMobile} from 'react-device-detect';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {LinesApi} from '../../core/api';
import {TRANSPORT_GROUP_ID, TRANSPORT_GROUP_KEY_MAP, TRANSPORT_ID, TRANSPORTS_KEY_MAP} from '../../core/constants';
import {RouteModel, ServiceModel, TimeTableResponse, AlertModel} from '../../core/models';
import {findBySlug} from '../../core/utils';
import {DataContext} from '../../services/data-v2/data.context';
import {HeaderContext} from '../../services/header/header.context';
import {MapContext} from '../../services/map-v2/map.context';
import {SpinnerContext} from '../../services/spinner/spinner.context';
import {translate} from '../../services/translation/translationUtils';
import {AmbIcon} from '../amb-icon/amb-icon.component';
import {BikeStopDetailComponent} from '../bike-stop-detail/bike-stop-detail.component';
import {List} from '../list/list.component';
import {ListItemInterface} from '../list/list.interface';
import MapPinInfo from '../map-pin-info/map-pin-info';
import {MotoStopDetailComponent} from '../moto-stop-detail/moto-stop-detail.component';
import {NavOptionsComponent} from '../nav-options/nav-options.component';
import {RealTimesComponent} from '../real-times/real-times.component';
import {ScheduleComponent} from '../schedule/schedule.component';
import {CarStopDetailComponent} from '../car-stop-detail/car-stop-detail.component';

import './stop-detail.scss';

interface TabInterface {
    id: number,
    translate: string,
    visible: boolean,
};

export const TABS: TabInterface[] = [
    {id: 1, translate: 'translation:transport_search_lines_title', visible: true},
    {id: 3, translate: 'translation:transport_line_detail_tab_schedules', visible: true},
    {id: 4, translate: 'translation:web_nav_access', visible: false},
];

export default function StopDetailComponent({buttons}: { buttons: string[] }) {

    const {stopId} = useParams<any>();

    const {
        ready,
        db,
        pinSet,
        services,
        currentStopAlerts,
        getCurrentStop,
        currentStop,
        realTimes,
        realTimeWorker,
    } = useContext(DataContext);
    const {initHeader} = useContext(HeaderContext);
    const {map} = useContext(MapContext);
    const {activeSpinner, deactivateSpinner} = useContext(SpinnerContext);

    const [selectedTab, setSelectedTab] = useState<TabInterface>(TABS[0]);
    const [routes, setRoutes] = useState<ListItemInterface<RouteModel>[]>([]);
    const [itineraries, setItineraries] = useState<ListItemInterface<RouteModel>[]>([]);
    const [timeTables, setTimeTables] = useState<TimeTableResponse[]>([]);
    const [distance, setDistance] = useState<number>();
    const [service, setService] = useState<ServiceModel | undefined>(findBySlug(services, currentStop?.slug));
    const [isSharedService, setIsSharedService] = useState<boolean>(false);
    const [sortedRoutes, setSortedRoutes] = useState<ListItemInterface<RouteModel>[]>([]);
    const [alerts, setAlerts] = useState<{
        hasAlert: boolean;
        alertId: string;
    }>({
        hasAlert: false,
        alertId: '',
    });

    const mapAlerts = useCallback((alerts: AlertModel[]) => {
        const alertIds = alerts.map((alert) => alert.id);
        const hasAlert = alertIds.length > 0;
        setAlerts({
            hasAlert,
            alertId: hasAlert ? alertIds[0] : '',
        });
    }, []);

    const getRoutes = useCallback(async () => {
        activeSpinner();

        const isSharedService = !TRANSPORT_GROUP_KEY_MAP[TRANSPORT_GROUP_ID.PUBLIC]?.slugs.includes(currentStop?.slug || '');

        if (!isSharedService && currentStop && realTimeWorker) {

            const {realTimes, timeTables} = await realTimeWorker.getPublicServiceStopRealTimes(currentStop);

            const routes = await db?.dataSources?.routes.findByStop(currentStop) || [];

            const list = routes.reduce((arr, route) => {

                const rrt = realTimes[route.id].filter((t) => t.lineCode === route.shortName);

                return [
                    ...arr,
                    ...(timeTables[route.id] ? timeTables[route.id].map((itinerary) => {
                        return {
                            id: route.id,
                            title: route.slug === 'renfe' ? itinerary.trips_headsign : (rrt[0] ? rrt[0].destination : itinerary.trips_headsign),
                            // title: rrt[0] ? rrt[0].destination :itinerary.trips_headsign,
                            // title: itinerary.trips_headsign,
                            subtitle: <RealTimesComponent stop={currentStop} route={route}
                                itinerary={itinerary}/>,
                            href: `/lines/${route.id}/stop/${currentStop.id}`,
                            icon: <AmbIcon
                                text={route.shortName}
                                color={route.textColor}
                                backgroundColor={route.color}
                                shape={route.slug === 'busamb' ? 'round' : 'square'}
                                size="m"/>,
                            data: route,
                            arrivalTime: rrt.find((t) => t.destination === itinerary.trips_headsign)?.arrivalTime,
                        };
                    }) : [{
                        id: route.id,
                        title: rrt[0] ? rrt[0].destination : route.longName,
                        subtitle: <RealTimesComponent stop={currentStop} route={route}/>,
                        href: `/lines/${route.id}/stop/${currentStop.id}`,
                        icon: <AmbIcon
                            text={route.shortName}
                            color={route.textColor}
                            backgroundColor={route.color}
                            shape={route.slug === 'busamb' ? 'round' : 'square'}
                            size="m"/>,
                        data: route,
                        arrivalTime: rrt[0]?.arrivalTime,
                    }]),
                ];
            }, [] as ListItemInterface<RouteModel>[])
                .sort((a: ListItemInterface<RouteModel>, b: ListItemInterface<RouteModel>) => {
                    return !a.arrivalTime ? 1 : !b.arrivalTime ? -1 : a.arrivalTime - b.arrivalTime;
                });

            setRoutes(routes.map((route) => ({
                id: route.id,
                title: route.longName,
                subtitle: <RealTimesComponent stop={currentStop} route={route}/>,
                href: `/lines/${route.id}/stop/${currentStop.id}`,
                icon: <AmbIcon
                    text={route.shortName}
                    color={route.textColor}
                    backgroundColor={route.color}
                    shape={route.slug === 'busamb' ? 'round' : 'square'}
                    size="m"/>,
                data: route,
            })));
            setItineraries(list);
            setTimeTables(await LinesApi.getStopTimeTable(currentStop));
        }

        setIsSharedService(isSharedService);

        deactivateSpinner();

    }, [db, currentStop, realTimeWorker]);

    const setHeader = useCallback(() => {
        let headerConfig = {};
        if (!isMobile && currentStop && pinSet) {
            headerConfig = {
                title: (isSharedService ? translate(service?.name) : currentStop.stopName) || '',
                backIcon: true,
                backToPrevius: false,
                closeIcon: false,
                logoIcon: false,
                icon: pinSet[currentStop.slug].icon,
                iconBackgroundWhite: pinSet[currentStop.slug].color || '',
                customClassName: {
                    'backgroundColor': 'var(--gris-fons-amb)',
                },
                backPath: '/home',
            };
            initHeader(headerConfig);
        }
    }, [currentStop, pinSet, isSharedService, service]);

    const routesHandler = useCallback(() => {

        setSortedRoutes([]);
        setTimeout(() => {
            if (currentStop) {

                const realTime = realTimes[currentStop.id];

                const sorted = [...routes].sort((a: ListItemInterface<RouteModel>, b: ListItemInterface<RouteModel>) => {

                    if (realTime) {
                        const rta = a.id ? realTime.realTimes[a.id] : null;
                        const rtb = b.id ? realTime.realTimes[b.id] : null;
                        return !rta || !rta[0] ? 1 : !rtb || !rtb[0] ? -1 : rta[0].arrivalTime - rtb[0].arrivalTime;
                    }
                    return 0;
                });

                if (realTime) {
                    sorted.map((elem: any, index: any) => {
                        // eslint-disable-next-line max-len
                        realTime.realTimes[elem.id]?.[0] !== undefined || '' ? sorted[index]['destination'] = realTime.realTimes[elem.id]?.[0].destination : sorted[index]['destination'] = '';
                    });
                }
                setSortedRoutes(sorted);
            }
        }, 50);
    }, [currentStop, routes, sortedRoutes, realTimes]);

    useEffect(() => {
        if (ready) getCurrentStop(stopId);
        return () => {
            getCurrentStop();
        };
    }, [stopId, ready]);

    useEffect(() => {
        if (currentStop) {
            setSelectedTab(TABS[0]);
            getRoutes();
        } else {
            setRoutes([]);
        }
    }, [currentStop]);

    useEffect(() => {
        setService(findBySlug(services, currentStop?.slug));
    }, [currentStop, services]);

    useEffect(() => {
        if (currentStop && service) {
            setHeader();
        }
    }, [currentStop, service]);

    useEffect(() => {
        routesHandler();
    }, [realTimes, routes]);

    useEffect(() => {
        if (currentStopAlerts) {
            mapAlerts(currentStopAlerts);
        }
    }, [currentStopAlerts, mapAlerts]);

    return <div className={`stop-detail-container${isMobile ? ' mobile' : ''}`}>
        {
            currentStop && pinSet && !isSharedService && <MapPinInfo
                icon={<AmbIcon
                    img={pinSet[currentStop.slug].icon}
                    backgroundColor={pinSet[currentStop.slug].color}
                    size="s"/>}
                title={currentStop.stopName}
                description={currentStop.stopCode}
                accessibility={currentStop.wheelchairBoarding}
                distance={distance}
                type={currentStop.stopType}
                slug={currentStop.slug}
                coord={{lat: currentStop.lat, lng: currentStop.lng}}
                buttons={buttons}
                hasAlert={alerts.hasAlert ? 'true' : 'false'}
                alertId={alerts.alertId}
            />
        }

        {
            currentStop && !isSharedService && <>
                <div className="pin_nav-tabs" style={{
                    ...(isMobile && {'padding': '0 20px'}),
                }}>
                    <NavOptionsComponent value={{
                        tabOptions: TABS,
                        changeOption: setSelectedTab,
                        selectedOption: selectedTab,
                    }}/>
                </div>

                <div className="route-list-container" style={{
                    ...(isMobile && {'padding': '0 20px'}),
                }}>
                    {
                        selectedTab.id == 1 && !!itineraries.length &&
                        <List items={itineraries} currentStopAlerts={currentStopAlerts}/>
                    }

                    {
                        selectedTab.id == 3 &&
                        <ScheduleComponent
                            routes={routes}
                            timeTables={timeTables}
                        />
                    }
                </div>
            </>
        }

        {
            currentStop && isSharedService &&
            <>

                {
                    TRANSPORTS_KEY_MAP[TRANSPORT_ID.BIKE]?.slugs.includes(currentStop.slug) &&
                    <BikeStopDetailComponent stop={currentStop}/>
                }

                {
                    [
                        ...(TRANSPORTS_KEY_MAP[TRANSPORT_ID.CAR]?.slugs || []),
                        ...(TRANSPORTS_KEY_MAP[TRANSPORT_ID.TAXI]?.slugs || []),
                        'endollat',
                    ].includes(currentStop.slug) &&
                    <CarStopDetailComponent stop={currentStop}/>
                }

                {
                    TRANSPORTS_KEY_MAP[TRANSPORT_ID.MOTO]?.slugs.includes(currentStop.slug) &&
                    <MotoStopDetailComponent stop={currentStop}/>
                }
            </>
        }
    </div>;
};
